import {
  capitalizeAllLetters, FORMAT_PPP,
  InspectionReportType,
  isInspectionTypeWithScopeNumber
} from '@inspectify/core';
import { FormControl, InputLabel, Select, SelectChangeEvent } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { ContractorTypeEnum } from 'pages/OnboardingFlow/types';
import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLayoutContext } from "../../../components/layouts/useLayoutContext";
import { useGetAvailableOrderReportsQuery, useGetContractorDetailsQuery } from '../../../gql/generated/queries';
import {
  selectContractorId,
  selectCurrentReport,
  selectReportIsPaywalled
} from '../../../redux/slices/reportSlice/report';
import { useAppSelector } from '../../../redux/store';
import { format } from "date-fns-tz";
import { fromUnixTime } from "date-fns";

const ReportSelector = () => {
  const { orderToken } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedReport = useAppSelector(selectCurrentReport);
  const propertyTimezone = useAppSelector(state => state.selectedReport.order.property?.timezone);
  const { printMode } = useLayoutContext();
  const { data } = useGetAvailableOrderReportsQuery({ orderToken: orderToken || '' }, { skip: !orderToken });
  const isPaywall = useAppSelector(selectReportIsPaywalled);

  const contractorId = useAppSelector(selectContractorId);
  const { data: contractorData } = useGetContractorDetailsQuery({ contractorId }, { skip: !contractorId });

  const isSelfInspection = contractorData?.contractor?.contractorType === ContractorTypeEnum.TeamInternal

  const onChange = useCallback(async (event: SelectChangeEvent<string>) => {
    navigate(`/orders/${orderToken}/reports/${event.target.value}${location.search}${location.hash}`);
  }, [orderToken, location.search, location.hash]);

  const uniqueInspectionReports = useMemo(() => {
    const reportsMap = data?.orderByToken?.inspections?.reduce((acc, inspection) => {
      inspection?.inspectionReports?.forEach(inspectionReport => {
        if (!inspectionReport?.report) return;

        // Only display unpublished reports for self-inspections
        if (isSelfInspection && inspectionReport.report.publishedAt === null) {
          return;
        }

        acc[inspectionReport.report.id!] = {
          ...inspectionReport,
          inspection: {
            inspectionScopeNumber: inspection.inspectionScopeNumber,
            inspectionType: inspection.inspectionType
          }
        } as InspectionReportType;
      });
      return acc;
    }, {} as Record<string, InspectionReportType>) || {};

    return Object.values(reportsMap).sort((a, b) => {
      const dateA = a.report?.publishedAt ?? a.report?.updatedAt ?? 0;
      const dateB = b.report?.publishedAt ?? b.report?.updatedAt ?? 0;
      return dateB - dateA;
    })
  }, [data, contractorData]);

  if (!selectedReport || !data?.orderByToken?.inspections?.length || printMode || isPaywall) return null;

  return (
    <FormControl>
      <InputLabel id="report-select-label">Currently Viewing</InputLabel>
      <Select<string>
        id="report-select"
        labelId="report-select-label"
        label="Currently Viewing"
        value={selectedReport?.id}
        disabled={(uniqueInspectionReports.length === 0 || !selectedReport)}
        autoWidth
        onChange={onChange}
      >
        {uniqueInspectionReports && Object.values(uniqueInspectionReports).map(inspectionReport => (
          <MenuItem key={inspectionReport.id} value={inspectionReport.report?.id}>
            <span>
              {isInspectionTypeWithScopeNumber(inspectionReport?.inspection?.inspectionType) &&
              inspectionReport?.inspection?.inspectionScopeNumber
                ? `${capitalizeAllLetters(inspectionReport.inspection?.inspectionType)} # ${inspectionReport?.inspection?.inspectionScopeNumber} - `
                : null}
              {format(fromUnixTime(inspectionReport.report?.publishedAt || inspectionReport.report?.updatedAt || 0), FORMAT_PPP, { timeZone: propertyTimezone || 'UTC' })}
              {inspectionReport?.report?.publishedAt
                ? ' (Published)'
                : inspectionReport?.report?.updatedAt
                  ? ' (Last updated)' : ''}
              <br />
            </span>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ReportSelector;
